import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from 'components/field/Button';
import CurrencyInput from 'components/field/CurrencyInput';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import ProgressWizard from 'components/field/ProgressWizard';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import Card from 'components/global/Card';
import LottiePlayer from 'components/global/LottiePlayer';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';

import fetchRemainingAmount from 'services/common/fetchRemainingAmount';
import { COD_WALLET, COMPANY, ENUMS } from 'services/consts';

import { setUserDetails } from 'stores/userSlice';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';
import PATHS from 'utils/paths';
import { validateIBAN, validateIBANFormat } from 'utils/utils';

import { faBuildingColumns, faCheck, faEdit } from '@fortawesome/pro-regular-svg-icons';
import { faAngleLeft, faAngleRight } from '@fortawesome/pro-solid-svg-icons';

import * as S from '../../CODWallet.styled';

/**
 * @author @muhammedburakak
 */

const BankDetails = ({ isLoading, handleCloseModal }) => {
	const navigate = useNavigate();

	const { Currency } = useLocale();

	const defaultColSpan = {
		md: 6
	};

	const formMethods = useFormContext();
	const { watch, setValue } = formMethods;

	return (
		<>
			<FormItem colSpan={defaultColSpan} label="currentBalance" isLoading={isLoading}>
				<CurrencyInput name="currentBalance" disabled />
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="withdrawAmount" isLoading={isLoading} required>
				<IntegerInput name="withdrawAmount" showArrows={false} allowFloat decimalLimit={2} unit={Currency} />
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="bankName" isLoading={isLoading} toolTip="bankName.description">
				<EnumSelect
					name="bankName"
					enumName={ENUMS.COUNTRY_BANKS}
					setValue={(value) => {
						setValue('bankName', value);
					}}
					disabled
					placeholder="selectOne"
					value={watch('bankName')}
					valueRemovable={false}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="bankHolderName" isLoading={isLoading} disabled>
				<StringInput name="bankHolderName" disabled />
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="bankIban" isLoading={isLoading} disabled>
				<StringInput name="bankIban" disabled />
			</FormItem>

			<FormItem>
				<Button
					label="editMyBankInformation"
					variant="blueBorderless"
					style={{ textDecoration: 'underline' }}
					icon={faEdit}
					onClick={() => {
						navigate(PATHS.GENERAL_SETTINGS);
						handleCloseModal();
					}}
				/>
			</FormItem>
		</>
	);
};

const Confirmation = ({ bankName, bankIban, bankHolderName, withdrawAmount, alertData }) => {
	const { t } = useTranslation();

	return (
		<ModalContent className="mt-5 mb-5" style={{ margin: 'auto', alignSelf: 'center' }}>
			<S.DeleteLottieContainer>
				<LottiePlayer name="confirmation-modal" width="100px" height="100px" />

				<Typography variant="p1" fontWeight="500" className="mb-3 mt-3">
					withdrawModal.title
				</Typography>

				{alertData?.alert && (
					<Alert className="mt-3" style={{ width: '65rem' }}>
						<Typography noWrap color="var(--alert-description)" fontWeight="500">
							{t(alertData?.alert, {
								fee: alertData?.fee
							})}
						</Typography>
					</Alert>
				)}
			</S.DeleteLottieContainer>

			<Card rounded className="mt-3">
				<S.CardContainer>
					<S.CardBody>
						<S.CardRow>
							<Typography variant="p2">bankHolderName</Typography>
							<Typography variant="p2" color="var(--content-3)">
								{bankHolderName ?? '-'}
							</Typography>
						</S.CardRow>
					</S.CardBody>

					<S.CardBody>
						<S.CardRow>
							<Typography variant="p2">bankName</Typography>
							<Typography variant="p2" color="var(--content-3)">
								{bankName ?? '-'}
							</Typography>
						</S.CardRow>
					</S.CardBody>

					<S.CardBody>
						<S.CardRow>
							<Typography variant="p2">bankIban</Typography>
							<Typography variant="p2" color="var(--content-3)">
								{bankIban ?? '-'}
							</Typography>
						</S.CardRow>
					</S.CardBody>

					<S.CardBody>
						<S.CardRow>
							<Typography variant="p2" fontWeight="500">
								withdrawAmount
							</Typography>
							<Typography withCurrency variant="p2" fontWeight="500">
								{withdrawAmount ?? 0}
							</Typography>
						</S.CardRow>
					</S.CardBody>
				</S.CardContainer>
			</Card>
		</ModalContent>
	);
};

const CODWithdrawModal = NiceModal.create(() => {
	const modal = useModal();
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const axiosPrivate = useAxiosPrivate();
	const { showSuccess, showError } = useAlert();

	const [isLoading, setIsLoading] = useState(false);
	const [alertData, setAlertData] = useState(null);
	const [tab, setTab] = useState(0);

	const { BankName, BankHolderName, BankIban } = useSelector((state) => state.user.generalSettings);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					bankName: yup.string().nullable(),
					bankHolderName: yup.string().nullable(),
					bankIban: yup.string().nullable(),
					withdrawAmount: yup
						.number()
						.min(20)
						.max(yup.ref('currentBalance'), ({ max }) => t('errorMsg.withdrawAmountMaxAmount', { max }))
						.required()
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			bankName: null,
			bankHolderName: null,
			bankIban: null,
			withdrawAmount: null
		}
	});

	const { watch, reset, handleSubmit, setValue, setError, clearErrors } = formMethods;

	const handleCloseModal = () => {
		modal.remove();
	};

	const getGeneralSettings = useCallback(async () => {
		setIsLoading(true);
		try {
			const response = await axiosPrivate.post(COMPANY.GET_GENERAL_SETTINGS);
			if (response.status === 200) {
				dispatch(setUserDetails({ generalSettings: response.data }));
				setValue('bankName', response.data.BankName);
				setValue('bankHolderName', response.data.BankHolderName);
				setValue('bankIban', response.data.BankIban);
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		} finally {
			setIsLoading(false);
		}
	}, []);

	const fillForm = async () => {
		setIsLoading(true);
		const { CODRemainingAmount } = await fetchRemainingAmount();

		reset({
			currentBalance: CODRemainingAmount ?? null,
			bankName: BankName,
			bankHolderName: BankHolderName,
			bankIban: BankIban
		});

		setIsLoading(false);
	};

	useEffect(() => {
		fillForm();
		getGeneralSettings();
	}, []);

	const bankInfoIsValid = () => {
		clearErrors('bankHolderName');
		clearErrors('bankIban');

		let error = false;

		// Validate IBAN
		const bankIBAN = watch('bankIban');

		if (!validateIBANFormat(bankIBAN)) {
			setError('bankIban', {
				type: 'manual',
				message: t('error.notValidIBANFormat')
			});

			error = true;
		} else if (!validateIBAN(bankIBAN)) {
			setError('bankIban', {
				type: 'manual',
				message: t('error.notValidIBAN')
			});

			error = true;
		}

		// Validate bank holder name
		const bankHolderName = watch('bankHolderName');

		if (bankHolderName.length > 35) {
			setError('bankHolderName', {
				type: 'manual',
				message: t('error.max35CharactersBankHolderName')
			});

			error = true;
		}

		return !error;
	};

	const onSubmit = async (data) => {
		setIsLoading(true);
		axiosPrivate
			.post(COD_WALLET.TRANSFER_TO_BANK_ACCOUNT, {
				amount: data.withdrawAmount,
				bankName: BankName,
				bankHolderName: BankHolderName,
				bankIban: BankIban
			})
			.then((response) => {
				if (response.status === 200) {
					showSuccess();
					fetchRemainingAmount(); // update redux state
					dispatch(updateDynamicListData(DYNAMICLISTS.COD_WALLET_LIST));
					handleCloseModal();
				} else {
					showError();
					setTab(0);
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				showError({
					message: error?.response?.data?.code ?? 'alert.anErrorOccurred'
				});
				AppLogger.error(error);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const nextStepAmount = async (data) => {
		setIsLoading(true);

		axiosPrivate
			.post(COD_WALLET.GET_TRANSFER_TO_BANK_NOTIFICATION, {
				amount: data.withdrawAmount
			})
			.then((response) => {
				if (Object.keys(response.data).length > 0) setAlertData(response.data);
			})
			.catch((error) => {
				showError();
				AppLogger.error(error);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const nextStep = async () => {
		if (tab === 0 && !bankInfoIsValid()) {
			return;
		}

		if (tab < 3 - 1) {
			await nextStepAmount(watch());
			setTab(tab + 1);
		}
	};

	const prevStep = () => {
		if (tab > 0) {
			setTab(tab - 1);
		}
	};

	const stages = useMemo(
		() => [
			{ name: 'withdrawModal.bankDetails', icon: faBuildingColumns },
			{ name: 'withdrawModal.selected', icon: faCheck }
		],
		[]
	);

	const wizardArr = [
		<BankDetails key="1" isLoading={isLoading} alertData={alertData} handleCloseModal={handleCloseModal} />,
		<Confirmation
			key="2"
			withdrawAmount={watch('withdrawAmount')}
			bankName={watch('bankName')}
			bankIban={watch('bankIban')}
			alertData={alertData}
			bankHolderName={watch('bankHolderName')}
		/>
	];

	return (
		<Modal fullwidth maxWidth="medium" className="modal-codWithdrawModal">
			<ModalTitle>
				<Typography variant="p1">withdraw</Typography>
				<ProgressWizard clickable={false} stages={stages} setIndex={setTab} index={tab} />
			</ModalTitle>

			<ModalContent>
				<Form formMethods={formMethods} borderless>
					{wizardArr[tab]}
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label="cancel" variant="borderless" onClick={handleCloseModal} />
				{tab !== 0 && (
					<Button
						label="previous"
						variant="outline"
						icon={faAngleLeft}
						onClick={() => prevStep()}
						loading={isLoading}
					/>
				)}
				{tab !== 1 && (
					<Button
						icon={faAngleRight}
						label="continue"
						iconPos="right"
						onClick={handleSubmit(nextStep, (errors) => {
							AppLogger.log(errors);
						})}
						variant="blue"
						loading={isLoading}
					/>
				)}
				{tab === 1 && (
					<Button
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/save-icon.json.gz"
						className="saveButton"
						label="confirm"
						iconPos="right"
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						variant="blue"
						loading={isLoading}
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

export default CODWithdrawModal;
